import React from "react";

import Centile from "pages/customer/tools/location/filters/targets/Centile";
import CentileMetric from "pages/customer/tools/location/filters/targets/CentileMetric";

import {
    selectCandidateComparatorStores,
    selectCandidateTarget,
    selectAggregatedDirectCompetitors,
    selectRetailCentresCentiles,
    selectStoreCategoryCentilesMetrics,
    setCandidateTarget
} from "modules/customer/tools/location/filters/filtersSlice";
import { useAppDispatch, useAppSelector } from "store";
import numberFormatter from "utils/numberFormatter";

const BrandPositioning: React.FC = () => {
    const dispatch = useAppDispatch();
    const candidateComparatorStores = useAppSelector(selectCandidateComparatorStores);
    const retailCentresCentiles = useAppSelector(selectRetailCentresCentiles);
    const comparatorStoresCentiles = candidateComparatorStores.map(store => {
        const retailCentreCentile = retailCentresCentiles.find(rcc => rcc.retailCentreId === store.retailCentre.id)?.brandPositioning ?? 0;
        return {
            storeName: store.name,
            centileValue: retailCentreCentile
        };
    });
    const aggregatedDirectCompetitors = useAppSelector(selectAggregatedDirectCompetitors);
    const aggregatedDirectCompetitorCentiles = aggregatedDirectCompetitors.map(item => ({
        storeName: item.fascia,
        centileValue: item.brandPositioningCentile
    }));
    const candidateTarget = useAppSelector(selectCandidateTarget);
    const storeCategoryCentilesMetrics = useAppSelector(selectStoreCategoryCentilesMetrics);
    const centileMetric = storeCategoryCentilesMetrics.find(cm => cm.centile === candidateTarget?.brandPositioning);
    const name = "Brand positioning";
    const description = "Brand positioning shows how retailers position themselves in the eyes of their customers.";
    const retailersCentileMetrics = [
        {
            name: "Luxury/Premium %",
            tooltip: "The percentage of stores in the Retail Zone with a brand type of luxury or premium.",
            value: numberFormatter.toPercentage(centileMetric?.luxuryOrPremiumPercentage ?? 0, false)
        }, {
            name: "Mainstream %",
            tooltip: "The percentage of stores in the Retail Zone with a brand type of mainstream.",
            value: numberFormatter.toPercentage(centileMetric?.mainstreamPercentage ?? 0, false)
        }, {
            name: "Value/Budget %",
            tooltip: "The percentage of stores in the Retail Zone with a brand type of value or budget.",
            value: numberFormatter.toPercentage(centileMetric?.valueOrBudgetPercentage ?? 0, false)
        }
    ];

    const handleSliderChange = (value: number) => {
        if (candidateTarget) {
            const newCandidateTarget = {
                ...candidateTarget,
                brandPositioning: value
            };
            dispatch(setCandidateTarget(newCandidateTarget));
        }
    };

    if (!candidateTarget) {
        return null;
    }

    return (
        <Centile
            name={name}
            description={description}
            disabled={!candidateTarget.useRetailers}
            sliderValue={candidateTarget.brandPositioning}
            onSliderChange={handleSliderChange}
            comparatorStoresCentiles={comparatorStoresCentiles}
            aggregatedDirectCompetitorCentiles={aggregatedDirectCompetitorCentiles}
            chartLowLabel="Low-end retailers"
            chartHighLabel="High-end retailers"
        >
            {retailersCentileMetrics.map((metric, index) =>
                <CentileMetric
                    key={index}
                    name={metric.name}
                    tooltip={metric.tooltip}
                    value={metric.value}
                    disabled={!candidateTarget.useRetailers}
                />
            )}
        </Centile>
    );
};

export default BrandPositioning;
